import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../Axios';
import Navbar from '../../components/Navbar';
import Toast from '../../services/Toast';
import Loader from '../../components/loader/Loader';
import './OrgUsers.css';

interface AppAccess {
  id: number;
  name: string;
  url: string;
}

interface OrgUser {
  id: number;
  user_name: string;
  email: string;
  role: number;
  organizations: OrgOption[];
  applications: AppAccess[];
}

interface FilterCounts {
  all: number;
  withAccess: number;
  withoutAccess: number;
}

interface OrgOption {
  id: number;
  name: string;
}

const getRoleLabel = (role: number) => {
  if (role === 2) return 'Super Admin';
  if (role === 1) return 'Admin';
  return 'Member';
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const APP_COLORS: Record<string, string> = {
  linentech: '#6366f1',
  linenconnect: '#f59e0b',
  lineniq: '#10b981',
  equipmate: '#3b82f6',
};

const APP_BG_COLORS: Record<string, string> = {
  linentech: '#eef2ff',
  linenconnect: '#fef3c7',
  lineniq: '#d1fae5',
  equipmate: '#dbeafe',
};

const AVATAR_GRADIENTS = [
  ['#6366f1', '#8b5cf6'],
  ['#3b82f6', '#06b6d4'],
  ['#10b981', '#0891b2'],
  ['#f59e0b', '#f97316'],
  ['#ec4899', '#a855f7'],
  ['#14b8a6', '#6366f1'],
  ['#f97316', '#ef4444'],
  ['#8b5cf6', '#6366f1'],
];

/**
 * Products that cannot stand on their own. linendesk reads linentech's own
 * databases, so revoking linentech soft-deletes the user there and linendesk
 * stops working with it — the two are not independent switches, and the drawer
 * should not present them as siblings.
 *
 * Keyed by lowercase application name, value is the product it depends on.
 */
const APP_DEPENDS_ON: Record<string, string> = { linendesk: 'linentech' };

/** Parents first, each followed by whatever depends on it. */
const orderByDependency = <T extends { name: string }>(apps: T[]): T[] => {
  const key = (a: T) => a.name?.toLowerCase() ?? '';
  const parents = apps.filter(a => !APP_DEPENDS_ON[key(a)]);
  const children = apps.filter(a => APP_DEPENDS_ON[key(a)]);
  const ordered: T[] = [];
  parents.forEach(parent => {
    ordered.push(parent);
    children.filter(c => APP_DEPENDS_ON[key(c)] === key(parent)).forEach(c => ordered.push(c));
  });
  // A dependant whose parent this organization does not hold still has to show.
  children.filter(c => !ordered.includes(c)).forEach(c => ordered.push(c));

  return ordered;
};

const getAppColor   = (name: string) => APP_COLORS[name?.toLowerCase()]   ?? '#5747ff';
const getAppColorBg = (name: string) => APP_BG_COLORS[name?.toLowerCase()] ?? '#eeecff';

const getAvatarGradient = (name: string) => {
  const hash = (name || 'U').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const getInitials = (name: string) =>
  name ? name.split(/[\s._-]/).map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';

const fmt = (n: number) => n.toLocaleString();

const getPageNumbers = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

type FilterType = 'all' | 'with-access' | 'without-access';

const OrgUsers: React.FC = () => {
  const navigate = useNavigate();

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isSuperAdmin = currentUser?.role === 2;

  const [users,          setUsers]         = useState<OrgUser[]>([]);
  const [orgApps,        setOrgApps]       = useState<AppAccess[]>([]);
  const [total,          setTotal]         = useState(0);
  const [counts,         setCounts]        = useState<FilterCounts>({ all: 0, withAccess: 0, withoutAccess: 0 });
  const [search,         setSearch]        = useState('');
  const [debouncedSearch,setDebouncedSearch] = useState('');
  const [filter,         setFilter]        = useState<FilterType>('all');
  const [page,           setPage]          = useState(1);
  const [pageSize,       setPageSize]      = useState(10);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading,   setTableLoading]  = useState(false);

  const [superAdminOrgs,    setSuperAdminOrgs]    = useState<OrgOption[]>([]);
  const [selectedOrgId,     setSelectedOrgId]     = useState<number | null>(null);

  const [drawerUser,    setDrawerUser]    = useState<OrgUser | null>(null);
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const [grantDialog,  setGrantDialog]  = useState(false);
  const [grantTarget,  setGrantTarget]  = useState<OrgUser | null>(null);
  const [grantAppId,   setGrantAppId]   = useState<number | ''>('');
  const [grantLoading, setGrantLoading] = useState(false);

  const [paletteOpen,  setPaletteOpen]  = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [paletteUsers, setPaletteUsers] = useState<OrgUser[]>([]);
  const paletteInputRef = useRef<HTMLInputElement>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Data helpers ─────────────────────────────────────────────────────────────

  const extractUsersResponse = (res: any) => {
    const outer = res?.data?.data;
    return {
      users:  (outer?.data   ?? []) as OrgUser[],
      total:  (outer?.total  ?? 0)  as number,
      counts: (outer?.counts ?? { all: 0, withAccess: 0, withoutAccess: 0 }) as FilterCounts,
    };
  };

  const extractArray = (res: any): any[] => {
    const d1 = res?.data;  if (Array.isArray(d1)) return d1;
    const d2 = d1?.data;   if (Array.isArray(d2)) return d2;
    const d3 = d2?.data;   if (Array.isArray(d3)) return d3;
    return [];
  };

  const fetchCurrentPage = async (p: number, lim: number, s: string, f: string): Promise<OrgUser[]> => {
    const params: any = { page: p, limit: lim, search: s, filter: f };
    if (isSuperAdmin && selectedOrgId) params.org_id = selectedOrgId;
    const res = await axiosInstance.get('/api/org/users', { params });
    const parsed = extractUsersResponse(res);
    setUsers(parsed.users);
    setTotal(parsed.total);
    setCounts(parsed.counts);
    return parsed.users;
  };

  // ── Super admin: fetch available orgs once ────────────────────────────────────

  useEffect(() => {
    if (!isSuperAdmin) return;
    axiosInstance.get('/api/org/my-organizations').then(res => {
      const orgs = extractArray(res) as OrgOption[];
      setSuperAdminOrgs(orgs);
      if (orgs.length > 0 && !selectedOrgId) setSelectedOrgId(orgs[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Org apps ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSuperAdmin && !selectedOrgId) return;
    const params: any = {};
    if (isSuperAdmin && selectedOrgId) params.org_id = selectedOrgId;
    axiosInstance.get('/api/org/applications', { params }).then(res => setOrgApps(extractArray(res))).catch(() => {});
  }, [selectedOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounce search ───────────────────────────────────────────────────────────

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // ── Main fetch ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSuperAdmin && !selectedOrgId) return;
    let alive = true;
    (async () => {
      setTableLoading(true);
      try {
        await fetchCurrentPage(page, pageSize, debouncedSearch, filter);
      } catch {
        if (alive) Toast.error('Failed to load organization data');
      } finally {
        if (alive) { setTableLoading(false); setInitialLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, [page, pageSize, debouncedSearch, filter, selectedOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setPaletteQuery(''); setPaletteUsers([]); setPaletteOpen(p => !p); }
      if (e.key === 'Escape') { setPaletteOpen(false); setDrawerOpen(false); setGrantDialog(false); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  useEffect(() => { if (paletteOpen) paletteInputRef.current?.focus(); }, [paletteOpen]);

  // ── Palette search ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!paletteOpen) return;
    if (!paletteQuery.trim()) { setPaletteUsers([]); return; }
    const t = setTimeout(async () => {
      try {
        const params: any = { page: 1, limit: 5, search: paletteQuery, filter: 'all' };
        if (isSuperAdmin && selectedOrgId) params.org_id = selectedOrgId;
        const res = await axiosInstance.get('/api/org/users', { params });
        setPaletteUsers(extractUsersResponse(res).users);
      } catch { /* silent */ }
    }, 200);
    return () => clearTimeout(t);
  }, [paletteQuery, paletteOpen]);

  // ── Drawer / actions ──────────────────────────────────────────────────────────

  const openDrawer = (u: OrgUser) => { setDrawerUser(u); setDrawerOpen(true); };

  const reloadAndRefresh = async (targetId: number) => {
    const updated = await fetchCurrentPage(page, pageSize, debouncedSearch, filter);
    const refreshed = updated.find(u => u.id === targetId);
    if (refreshed) setDrawerUser(refreshed);
  };

  const handleRevoke = async (targetUser: OrgUser, appId: number) => {
    setActionLoading(appId);
    try {
      const params: any = {};
      if (isSuperAdmin && selectedOrgId) params.org_id = selectedOrgId;
      await axiosInstance.delete(`/api/org/users/${targetUser.id}/access/${appId}`, { params });
      Toast.success('Access revoked');
      await reloadAndRefresh(targetUser.id);
    } finally { setActionLoading(null); }
  };

  const handleGrantDirect = async (targetUser: OrgUser, appId: number) => {
    setActionLoading(appId);
    try {
      const body: any = { application_id: appId };
      if (isSuperAdmin && selectedOrgId) body.org_id = selectedOrgId;
      await axiosInstance.post(`/api/org/users/${targetUser.id}/access`, body);
      Toast.success('Access granted');
      await reloadAndRefresh(targetUser.id);
    } finally { setActionLoading(null); }
  };

  const openGrantDialog = (u: OrgUser) => { setGrantTarget(u); setGrantAppId(''); setGrantDialog(true); };

  const handleGrantDialog = async () => {
    if (!grantTarget || !grantAppId) return;
    setGrantLoading(true);
    try {
      const body: any = { application_id: grantAppId };
      if (isSuperAdmin && selectedOrgId) body.org_id = selectedOrgId;
      await axiosInstance.post(`/api/org/users/${grantTarget.id}/access`, body);
      Toast.success('Access granted');
      setGrantDialog(false);
      await reloadAndRefresh(grantTarget.id);
    } finally { setGrantLoading(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (initialLoading) return <div className="ou-loading"><Loader /></div>;

  return (
    <>
      <Navbar />

      <div className="ou-page">
        <div className="ou-container max-width">

          {/* ── Heading ── */}
          <div className="ou-heading">
            <button className="ou-back-btn" onClick={() => navigate('/home')} data-cy="org-users-back-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
              Back
            </button>
            <div className="ou-heading__main">
              <div className="ou-heading__icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div>
                <h1 className="ou-title">Organization Users</h1>
                <p className="ou-subtitle">Manage product access for your organization members</p>
              </div>
            </div>
          </div>

          {/* ── Super Admin: org selector ── */}
          {isSuperAdmin && superAdminOrgs.length > 0 && (
            <div className="ou-org-selector">
              <label className="ou-org-selector__label">Organization</label>
              <select
                className="ou-org-selector__select"
                value={selectedOrgId ?? ''}
                onChange={e => { setSelectedOrgId(Number(e.target.value)); setPage(1); }}
                data-cy="org-selector"
              >
                {superAdminOrgs.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* ── Stat cards (act as filter tabs) ── */}
          <div className="ou-stats">
            <button
              className={`ou-stat${filter === 'all' ? ' ou-stat--active' : ''}`}
              onClick={() => { setFilter('all'); setPage(1); }}
              data-cy="filter-all-btn"
            >
              <div className="ou-stat__icon ou-stat__icon--purple">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div className="ou-stat__body">
                <div className="ou-stat__num">{fmt(counts.all)}</div>
                <div className="ou-stat__label">Total Members</div>
              </div>
            </button>

            <button
              className={`ou-stat${filter === 'with-access' ? ' ou-stat--active ou-stat--green' : ''}`}
              onClick={() => { setFilter('with-access'); setPage(1); }}
              data-cy="filter-with-access-btn"
            >
              <div className="ou-stat__icon ou-stat__icon--green">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div className="ou-stat__body">
                <div className="ou-stat__num">{fmt(counts.withAccess)}</div>
                <div className="ou-stat__label">With Access</div>
              </div>
            </button>

            <button
              className={`ou-stat${filter === 'without-access' ? ' ou-stat--active ou-stat--amber' : ''}`}
              onClick={() => { setFilter('without-access'); setPage(1); }}
              data-cy="filter-without-access-btn"
            >
              <div className="ou-stat__icon ou-stat__icon--amber">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div className="ou-stat__body">
                <div className="ou-stat__num">{fmt(counts.withoutAccess)}</div>
                <div className="ou-stat__label">No Access</div>
              </div>
            </button>
          </div>

          {/* ── Search + ⌘K ── */}
          <div className="ou-searchbar">
            <svg className="ou-searchbar__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              className="ou-searchbar__input"
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-cy="org-users-search-input"
            />
            {search && (
              <button className="ou-searchbar__clear" onClick={() => { setSearch(''); setDebouncedSearch(''); setPage(1); }} data-cy="org-users-search-clear-btn">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            )}
            <div className="ou-searchbar__divider" />
            <button className="ou-searchbar__cmd" onClick={() => { setPaletteQuery(''); setPaletteUsers([]); setPaletteOpen(true); }} data-cy="org-users-quicksearch-btn">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              Quick search
              <kbd>⌘K</kbd>
            </button>
          </div>

          {/* ── Table card ── */}
          <div className="ou-card">
            <div className={`ou-table-wrap${tableLoading ? ' ou-table-wrap--loading' : ''}`}>
              {users.length === 0 && !tableLoading ? (
                <div className="ou-empty">
                  <div className="ou-empty__icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <p className="ou-empty__title">No users found</p>
                  {search && <p className="ou-empty__hint">Try a different search term</p>}
                </div>
              ) : (
                <table className="ou-table" data-cy="org-users-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Organization</th>
                      <th>Role</th>
                      <th>App Access</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => {
                      const [g1, g2] = getAvatarGradient(u.user_name);
                      return (
                        <tr key={u.id} className="ou-row" onClick={() => openDrawer(u)} data-cy={`org-user-row-${u.id}`}>
                          <td>
                            <div className="ou-member">
                              <div
                                className="ou-avatar"
                                style={{ background: `linear-gradient(135deg, ${g1}, ${g2})` }}
                              >
                                {getInitials(u.user_name)}
                              </div>
                              <div>
                                <div className="ou-member__name">{u.user_name || '—'}</div>
                                <div className="ou-member__email">{u.email || '—'}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="ou-chips">
                              {u.organizations.slice(0, 2).map(o => (
                                <span key={o.id} className="ou-chip ou-chip--org">{o.name}</span>
                              ))}
                              {u.organizations.length > 2 && (
                                <span className="ou-chip ou-chip--more">+{u.organizations.length - 2}</span>
                              )}
                              {u.organizations.length === 0 && (
                                <span className="ou-no-access">—</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`ou-role-badge${u.role >= 1 ? ' ou-role-badge--admin' : ''}`}>
                              {getRoleLabel(u.role)}
                            </span>
                          </td>
                          <td>
                            <div className="ou-chips">
                              {u.applications.slice(0, 3).map(a => (
                                <span
                                  key={a.id}
                                  className="ou-chip"
                                  style={{ color: getAppColor(a.name), backgroundColor: getAppColorBg(a.name) }}
                                >
                                  {a.name}
                                </span>
                              ))}
                              {u.applications.length > 3 && (
                                <span className="ou-chip ou-chip--more">+{u.applications.length - 3}</span>
                              )}
                              {u.applications.length === 0 && (
                                <span className="ou-no-access">No access</span>
                              )}
                            </div>
                          </td>
                          <td className="ou-row__action">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="ou-pagination">
                <div className="ou-pagination__left">
                  <span className="ou-pagination__info">
                    {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {fmt(total)} members
                  </span>
                  <label className="ou-page-size-label">
                    Rows per page
                    <select
                      className="ou-page-size-select"
                      value={pageSize}
                      onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                      data-cy="page-size-select"
                    >
                      {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                </div>
                {totalPages > 1 && (
                  <div className="ou-pagination__btns">
                    <button className="ou-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-cy="pagination-prev-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                    {getPageNumbers(page, totalPages).map((n, i) =>
                      n === '...'
                        ? <span key={`e-${i}`} className="ou-page-ellipsis">…</span>
                        : <button key={n} className={`ou-page-btn${page === n ? ' ou-page-btn--active' : ''}`} onClick={() => setPage(n)} data-cy={`pagination-page-btn-${n}`}>{n}</button>
                    )}
                    <button className="ou-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} data-cy="pagination-next-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Side Drawer ── */}
      {drawerOpen && drawerUser && (
        <>
          <div className="ou-backdrop" onClick={() => setDrawerOpen(false)} />
          <aside className="ou-drawer">
            <div className="ou-drawer__header">
              <div className="ou-drawer__profile">
                {(() => { const [g1, g2] = getAvatarGradient(drawerUser.user_name); return (
                  <div className="ou-drawer__avatar" style={{ background: `linear-gradient(135deg, ${g1}, ${g2})` }}>
                    {getInitials(drawerUser.user_name)}
                  </div>
                ); })()}
                <div className="ou-drawer__info">
                  <div className="ou-drawer__name">{drawerUser.user_name || '—'}</div>
                  <div className="ou-drawer__email">{drawerUser.email || '—'}</div>
                  <span className={`ou-role-badge${drawerUser.role >= 1 ? ' ou-role-badge--admin' : ''}`}>
                    {getRoleLabel(drawerUser.role)}
                  </span>
                </div>
              </div>
              <button className="ou-drawer__close" onClick={() => setDrawerOpen(false)} data-cy="drawer-close-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>

            <div className="ou-drawer__body">
              <div className="ou-drawer__section-head">
                <span>App Access</span>
                <span className="ou-drawer__count">{drawerUser.applications.length} / {orgApps.length}</span>
              </div>

              {orgApps.length === 0
                ? <p className="ou-drawer__empty">No applications licensed to this organization.</p>
                : (
                  <div className="ou-drawer__apps">
                    {orderByDependency(orgApps).map(app => {
                      const hasAccess = drawerUser.applications.some(a => a.id === app.id);
                      const busy = actionLoading === app.id;
                      const parent = APP_DEPENDS_ON[app.name?.toLowerCase()];
                      return (
                        <div
                          key={app.id}
                          className={`ou-drawer__app-row${hasAccess ? ' ou-drawer__app-row--active' : ''}${parent ? ' ou-drawer__app-row--child' : ''}`}
                          title={parent ? `Requires ${parent}. Revoking ${parent} revokes this too.` : undefined}
                        >
                          <div className="ou-drawer__app-left">
                            <div className="ou-drawer__app-dot" style={{ background: getAppColor(app.name) }} />
                            <span className="ou-drawer__app-name">{app.name}</span>
                          </div>
                          {hasAccess
                            ? <button className="ou-revoke-btn" disabled={busy} onClick={() => handleRevoke(drawerUser, app.id)} data-cy={`drawer-revoke-btn-${app.id}`}>{busy ? '…' : 'Revoke'}</button>
                            : <button className="ou-grant-btn"  disabled={busy} onClick={() => handleGrantDirect(drawerUser, app.id)} data-cy={`drawer-grant-btn-${app.id}`}>{busy ? '…' : 'Grant'}</button>
                          }
                        </div>
                      );
                    })}
                  </div>
                )
              }

              {orgApps.some(app => !drawerUser.applications.some(a => a.id === app.id)) && (
                <button className="ou-drawer__assign-btn" onClick={() => openGrantDialog(drawerUser)} data-cy="drawer-assign-dialog-btn">
                  + Assign via dialog
                </button>
              )}
            </div>
          </aside>
        </>
      )}

      {/* ── Grant Access Dialog ── */}
      {grantDialog && grantTarget && (
        <>
          <div className="ou-backdrop ou-backdrop--dialog" onClick={() => setGrantDialog(false)} />
          <div className="ou-dialog">
            <div className="ou-dialog__header">
              <h3 className="ou-dialog__title">Grant Access</h3>
              <button className="ou-dialog__close" onClick={() => setGrantDialog(false)} data-cy="grant-dialog-close-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="ou-dialog__body">
              <p className="ou-dialog__desc">Select an application to grant <strong>{grantTarget.user_name}</strong> access to.</p>
              <label className="ou-dialog__label">Application</label>
              <select className="ou-dialog__select" value={grantAppId} onChange={e => setGrantAppId(Number(e.target.value))} data-cy="grant-dialog-app-select">
                <option value="">Choose an application…</option>
                {orgApps.filter(a => !grantTarget.applications.some(ua => ua.id === a.id)).map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {orgApps.filter(a => !grantTarget.applications.some(ua => ua.id === a.id)).length === 0 && (
                <p className="ou-dialog__all-granted">All available apps are already granted.</p>
              )}
            </div>
            <div className="ou-dialog__footer">
              <button className="ou-dialog__cancel" onClick={() => setGrantDialog(false)} data-cy="grant-dialog-cancel-btn">Cancel</button>
              <button className="ou-dialog__confirm" disabled={!grantAppId || grantLoading} onClick={handleGrantDialog} data-cy="grant-dialog-confirm-btn">
                {grantLoading ? 'Granting…' : 'Grant Access'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Command Palette ── */}
      {paletteOpen && (
        <>
          <div className="ou-backdrop ou-backdrop--center" onClick={() => setPaletteOpen(false)} />
          <div className="ou-palette">
            <div className="ou-palette__search-row">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input ref={paletteInputRef} className="ou-palette__input" placeholder="Search members…" value={paletteQuery} onChange={e => setPaletteQuery(e.target.value)} data-cy="palette-search-input" />
              <button className="ou-palette__esc" onClick={() => setPaletteOpen(false)} data-cy="palette-close-btn">ESC</button>
            </div>
            <div className="ou-palette__results">
              {paletteUsers.length === 0 ? (
                <div className="ou-palette__empty">{paletteQuery ? `No results for "${paletteQuery}"` : 'Start typing to search…'}</div>
              ) : (
                <>
                  <div className="ou-palette__section">Members</div>
                  {paletteUsers.map(u => {
                    const [g1, g2] = getAvatarGradient(u.user_name);
                    return (
                      <button key={u.id} className="ou-palette__item" onClick={() => { setPaletteOpen(false); openDrawer(u); }} data-cy={`palette-user-item-${u.id}`}>
                        <div className="ou-palette__avatar" style={{ background: `linear-gradient(135deg, ${g1}, ${g2})` }}>{getInitials(u.user_name)}</div>
                        <div className="ou-palette__item-text">
                          <div className="ou-palette__item-name">{u.user_name || '—'}</div>
                          <div className="ou-palette__item-sub">{u.email || 'No email'}</div>
                        </div>
                        <span className="ou-palette__item-badge">{u.applications.length} apps</span>
                      </button>
                    );
                  })}
                  {orgApps.length > 0 && (
                    <>
                      <div className="ou-palette__section">Applications</div>
                      {orgApps.filter(a => !paletteQuery || a.name.toLowerCase().includes(paletteQuery.toLowerCase())).slice(0, 4).map(a => (
                        <div key={a.id} className="ou-palette__item ou-palette__item--app">
                          <div className="ou-palette__app-dot" style={{ background: getAppColor(a.name) }} />
                          <span className="ou-palette__item-name">{a.name}</span>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default OrgUsers;
